import { T } from "@/helpers/translator";
import { ASSISTANT_UI_TRANSLATIONS, assistantUiText } from "./assistantUiTranslations";

const ASSISTANT_LABEL_ALIASES = {
	"Approval required": "approval.required",
	"Action executed": "action.executed",
	"Action rejected": "action.rejected",
	Result: "result",
	Results: "results",
	"Related Lists": "problemRelatedList",
	relatedLists: "problemRelatedList"
};

const TRANSLATION_KEY_BY_LABEL = {
	desc: "description",
	description: "description",
	title: "title",
	status: "status",
	note: "note",
	days: "days",
	start: "start",
	end: "end",
	location: "location",
	calendar: "calendar",
	priority: "priority",
	type: "type",
	department: "department",
	room: "room",
	message: "message"
};

const ASSISTANT_UI_KEY_BY_LABEL = {
	"approval.required": "approvalRequired",
	"action.executed": "actionExecutedLabel",
	"action.rejected": "actionRejectedLabel",
	approve: "approveLabel",
	reject: "rejectLabel",
	pending: "pendingLabel",
	approved: "approvedLabel",
	expired: "expiredLabel",
	rejected: "rejectedLabel",
	executed: "executedLabel",
	failed: "failedLabel",
	result: "resultLabel",
	results: "resultsLabel",
	"summary.writeConfirmation": "writeConfirmation",
	"summary.approveAction": "approveAction"
};

export function activeLangCode(lang) {
	const code = typeof lang === "string" ? lang : lang?.lang || lang?.value || lang?.code || "en";
	return String(code).toLowerCase().split(/[-_]/)[0];
}

export function assistantDirection(lang) {
	return ["ar", "fa", "he"].includes(activeLangCode(lang)) ? "rtl" : "ltr";
}

function localAssistantText(key, lang) {
	const uiKey = ASSISTANT_UI_KEY_BY_LABEL[key] || key;
	return ASSISTANT_UI_TRANSLATIONS[uiKey] ? assistantUiText(uiKey, lang) : null;
}

function humanizeLabel(value) {
	return String(value || "")
		.replace(/\./g, " ")
		.replace(/_/g, " ")
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.trim()
		.replace(/\s+/g, " ")
		.replace(/^./, (char) => char.toUpperCase());
}

// Recognize API display labels as well as canonical keys, including saved history.
const ENGLISH_LABEL_KEYS = Object.fromEntries(
	Object.entries(ASSISTANT_UI_TRANSLATIONS).flatMap(([key, translations]) => [
		[translations.en.toLowerCase(), key],
		...(key.includes(".") ? [[humanizeLabel(key).toLowerCase(), key]] : [])
	])
);

export function translateAssistantLabel(label, lang) {
	if (label == null || label === "") return "-";
	const raw = String(label);
	const key = ASSISTANT_LABEL_ALIASES[raw] || ENGLISH_LABEL_KEYS[raw.toLowerCase()] || (ASSISTANT_UI_TRANSLATIONS[raw.toLowerCase()] ? raw.toLowerCase() : raw);
	const local = localAssistantText(key, lang);
	if (local) return local;
	if (key.endsWith("Draft") && ASSISTANT_UI_TRANSLATIONS[key.slice(0, -5)]) {
		return assistantUiText("draftAction", lang, { action: assistantUiText(key.slice(0, -5), lang) });
	}
	const translationKey = TRANSLATION_KEY_BY_LABEL[key] || TRANSLATION_KEY_BY_LABEL[key.toLowerCase()] || key;
	const translated = T(translationKey, activeLangCode(lang));
	return translated !== translationKey ? translated : humanizeLabel(key);
}

export function translateAssistantText(text, lang) {
	if (!text) return text;
	const normalized = String(text).trim();
	if (activeLangCode(lang) === "hy") {
		// Older persisted responses predate server-side localization.
		const messages = {
			"No matching HR records were found.": "Համապատասխան աշխատակցային տվյալներ չեն գտնվել։",
			"Try an employee name, department, position, date, or request status.": "Փորձեք աշխատակցի անունը, բաժինը, պաշտոնը, ամսաթիվը կամ հարցման կարգավիճակը։",
			"Done.": "Կատարված է։"
		};
		if (messages[normalized]) return messages[normalized];
		if (normalized.includes("\n")) return normalized.split("\n").map((line) => translateAssistantText(line, lang)).join("\n");
		const sent = normalized.match(/^Notification sent to (.+)\.$/);
		if (sent) return `Ծանուցումն ուղարկված է՝ ${sent[1]}։`;
	}
	if (normalized === "Write-capable AI action requires your confirmation.") {
		return localAssistantText("summary.writeConfirmation", lang);
	}
	if (/^Please approve this(?: .*?)? action before I make changes\.$/i.test(normalized)) {
		return localAssistantText("summary.approveAction", lang);
	}
	const prepared = normalized.match(/^I prepared ([\w.]+)\. Please approve before I make changes\.$/);
	if (prepared && (ASSISTANT_UI_TRANSLATIONS[prepared[1]] || (prepared[1].endsWith("Draft") && ASSISTANT_UI_TRANSLATIONS[prepared[1].slice(0, -5)]))) {
		return assistantUiText("preparedAction", lang, { action: translateAssistantLabel(prepared[1], lang) });
	}
	return text;
}

